import React, { ReactNode, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';

interface SafeRouteProps {
  children: ReactNode;
  requiresData?: boolean; // If true, shows loading state when location.state is null
  fallback?: ReactNode; // Custom loading component
  timeout?: number; // How long to wait for data before showing error (default: 5000ms)
  onDataMissing?: () => void; // Callback when data is missing after timeout
  enableSessionStorage?: boolean; // Try to recover from sessionStorage
  sessionStorageKey?: string; // Key to use for session storage
  minLoadingTime?: number; // Minimum time to show loading (prevents flash)
}

const SafeRoute: React.FC<SafeRouteProps> = ({
  children,
  requiresData = false,
  fallback,
  timeout = 5000,
  onDataMissing,
  enableSessionStorage = true,
  sessionStorageKey = 'route-data',
  minLoadingTime = 300
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(requiresData);
  const [hasData, setHasData] = useState(!requiresData);
  const [showError, setShowError] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(!requiresData);

  useEffect(() => {
    if (!requiresData) return;

    let timeoutId: NodeJS.Timeout;
    let minTimeId: NodeJS.Timeout;

    // Check if we have data from route state
    const checkForData = () => {
      let dataFound = !!location.state;

      // Try session storage if enabled and no route data
      if (!dataFound && enableSessionStorage) {
        try {
          const storedData = sessionStorage.getItem(sessionStorageKey);
          if (storedData) {
            dataFound = true;
            console.log(`SafeRoute: Recovered data from sessionStorage with key: ${sessionStorageKey}`);
          }
        } catch (error) {
          console.error('SafeRoute: Failed to read from sessionStorage:', error);
        }
      }

      if (dataFound) {
        setHasData(true);
        // Wait for minimum time before hiding loading
        if (minTimeElapsed) {
          setIsLoading(false);
        }
      } else {
        // Start timeout for missing data
        timeoutId = setTimeout(() => {
          setShowError(true);
          setIsLoading(false);
          onDataMissing?.();
        }, timeout);
      }
    };

    // Minimum loading time to prevent flash
    minTimeId = setTimeout(() => {
      setMinTimeElapsed(true);
      if (hasData) {
        setIsLoading(false);
      }
    }, minLoadingTime);

    checkForData();

    // Cleanup
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (minTimeId) clearTimeout(minTimeId);
    };
  }, [location.state, requiresData, enableSessionStorage, sessionStorageKey, timeout, minLoadingTime, onDataMissing, hasData, minTimeElapsed]);

  // Show loading state
  if (isLoading) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Loading...
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Please wait while we prepare your content.
          </p>
        </div>
      </div>
    );
  }

  // Show error state if data is required but missing
  if (requiresData && showError) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6 text-center">
            <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Page Data Missing
            </h2>
            
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              The data needed to display this page is not available. This might happen if you navigated here directly or the session expired.
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => window.history.back()}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Wrap children in error boundary
  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        console.error('SafeRoute error boundary triggered:', error, errorInfo);
      }}
      resetKeys={[location.pathname, location.search]}
      resetOnPropsChange
    >
      {children}
    </ErrorBoundary>
  );
};

export default SafeRoute;

// Helper hook to store route data in session storage
export function useRouteData(key: string = 'route-data') {
  const storeData = (data: any) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(data));
      console.log(`Stored route data with key: ${key}`);
    } catch (error) {
      console.error('Failed to store route data:', error);
    }
  };

  const retrieveData = () => {
    try {
      const stored = sessionStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Failed to retrieve route data:', error);
      return null;
    }
  };

  const clearData = () => {
    try {
      sessionStorage.removeItem(key);
      console.log(`Cleared route data with key: ${key}`);
    } catch (error) {
      console.error('Failed to clear route data:', error);
    }
  };

  return { storeData, retrieveData, clearData };
}